import React from "react";
import { defineMessages, useIntl } from "react-intl";
import Button, { ButtonProps } from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import TranslateIcon from "@material-ui/icons/Translate";

import { Locale } from "../EnhancedIntlProvider";
import useEnhancedIntl from "../../hooks/useEnhancedIntl";

const msg = defineMessages({
  localeSwitcherButtonTooltipLabel: {
    id: "localeSwitcher.ButtonTooltip.label",
    description: "Label of the button tooltip.",
    defaultMessage: "Change language",
  },
});

export type LocaleSwitcherProps = ButtonProps;

/**
 * Let the user choose his preferred language.
 *
 * @param props - The LocaleSwitcher component props.
 */
function LocaleSwitcher(props: LocaleSwitcherProps) {
  const { ...other } = props;
  const { formatMessage: t } = useIntl();
  const { supportedLocales, locale, setLocale } = useEnhancedIntl();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = React.useCallback(
    (_event: {}, _reason: "backdropClick" | "escapeKeyDown") => {
      setAnchorEl(null);
    },
    []
  );

  const handleMenuItemClick = React.useCallback(
    (locale: Locale) => () => {
      setLocale(locale);
      setAnchorEl(null);
    },
    [setLocale]
  );

  return (
    <>
      <Tooltip title={t(msg.localeSwitcherButtonTooltipLabel)}>
        <Button
          aria-controls="locale-switcher-menu"
          aria-haspopup="menu"
          aria-label={t(msg.localeSwitcherButtonTooltipLabel)}
          onClick={handleClick}
          startIcon={<TranslateIcon />}
          {...other}
        >
          {supportedLocales[locale]}
        </Button>
      </Tooltip>
      <Menu
        id="locale-switcher-menu"
        anchorEl={anchorEl}
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {Object.keys(supportedLocales).map((key) => {
          const supportedLocale = key as Locale;
          const supportedLocaleLabel = supportedLocales[supportedLocale];

          return (
            <MenuItem
              key={supportedLocale}
              selected={locale === supportedLocale}
              onClick={handleMenuItemClick(supportedLocale)}
            >
              {`${supportedLocaleLabel} (${supportedLocale})`}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default LocaleSwitcher;
